/**
 * Canvas size hook — responsive layout with three breakpoints.
 * Delegates sizing to computeResponsiveLayout for portrait/landscape/desktop.
 */

import { useCallback, useEffect, useState } from "react";
import {
  computeResponsiveLayout,
  type LayoutMode,
  type ResponsiveLayout,
} from "../utils/responsive-layout";

const CANVAS_PADDING = 36;

export interface CanvasSize {
  readonly width: number;
  readonly height: number;
  readonly padding: number;
  readonly layoutMode: LayoutMode;
  readonly controllerSize: number;
  readonly showSidePanel: boolean;
}

function toCanvasSize(layout: ResponsiveLayout): CanvasSize {
  return {
    width: layout.canvasSize,
    height: layout.canvasSize,
    padding: CANVAS_PADDING,
    layoutMode: layout.layoutMode,
    controllerSize: layout.controllerSize,
    showSidePanel: layout.showSidePanel,
  };
}

export function useCanvasSize(): CanvasSize {
  const [size, setSize] = useState<CanvasSize>(() =>
    toCanvasSize(
      computeResponsiveLayout(window.innerWidth, window.innerHeight),
    ),
  );

  const handleResize = useCallback(() => {
    setSize(
      toCanvasSize(
        computeResponsiveLayout(window.innerWidth, window.innerHeight),
      ),
    );
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const debouncedResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };
    window.addEventListener("resize", debouncedResize);
    return () => {
      window.removeEventListener("resize", debouncedResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [handleResize]);

  return size;
}
